import type { FlexComponent } from "@/utils/render-component";
import { computed, defineComponent, nextTick, onUnmounted, ref, watch } from "vue";
import { useShortcutOverlay } from "@/composables/shortcut-overlay";
import { $t } from "@/services/i18n";
import { renderComponent } from "@/utils/render-component";
import Dialog from "../primitives/dialog/Dialog";
import Button from "../ui/button/Button";
import Form from "../ui/form/Form";

export const modalSizes = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  "8xl": "max-w-8xl",
  "9xl": "max-w-9xl",
  "screen-sm": "max-w-(--breakpoint-sm)",
  "screen-md": "max-w-3xl",
  "screen-lg": "max-w-5xl",
  "screen-xl": "max-w-(--breakpoint-xl) max-w-[calc(100vw-32px)]",
  "screen-2xl": "max-w-(--breakpoint-2xl) max-w-[calc(100vw-32px)]",
  full: "h-screen w-screen",
  cover: "h-[calc(100vh-32px)] max-w-[calc(100vw-32px)]",
};

interface ModalWrapper {
  title: string;
  description?: string;
  onSubmit: () => void;
  btnLabel?: string;
}

export type ModalSize = keyof typeof modalSizes;

const activeModal = ref<FlexComponent | null>(null);

/** Whether the one-modal provider currently has an active modal. */
export const isModalOpen = computed(() => !!activeModal.value);
const size = ref<ModalSize | undefined>(undefined);
const wrapper = ref<ModalWrapper | undefined>(undefined);
const noBackdrop = ref(false);
const disableOutsideClose = ref(false);
const showClose = ref(true);

let focusedElementBeforeModal: Element | null = null;

export function openModal(
  component: FlexComponent,
  options?: {
    size?: ModalSize;
    wrapper?: ModalWrapper;
    noBackdrop?: boolean;
    disableOutsideClose?: boolean;
    showClose?: boolean;
  }
) {
  if (activeModal.value) {
    activeModal.value = null;
    setTimeout(() => {
      openModal(component, options);
    }, 100);
    return;
  }

  focusedElementBeforeModal = document.activeElement;

  activeModal.value = component;
  size.value = options?.size;
  wrapper.value = options?.wrapper;
  noBackdrop.value = options?.noBackdrop ?? false;
  disableOutsideClose.value = options?.disableOutsideClose ?? false;
  showClose.value = options?.showClose ?? true;
}

export function closeModal() {
  activeModal.value = null;

  if (focusedElementBeforeModal) {
    nextTick(() => {
      (focusedElementBeforeModal as HTMLElement)?.focus();
      focusedElementBeforeModal = null;
    });
  }
}

export default defineComponent({
  name: "ModalProvider",
  setup() {
    // A provider modal is a shortcut overlay: page-level shortcuts suspend while
    // it is open, while an explicitly `overlayOwned` modal shortcut still works.
    useShortcutOverlay(isModalOpen);

    const open = computed({
      get: () => !!activeModal.value,
      set: (value) => {
        if (!value) closeModal();
      },
    });

    watch(activeModal, (value) => {
      if (value) open.value = true;
      else open.value = false;
    });

    onUnmounted(() => {
      closeModal();
    });

    const style = computed(() => {
      if (!size.value) return "max-w-md";
      if (["cover", "full"].includes(size.value))
        return "max-h-[calc(100vh-32px)] " + modalSizes[size.value];
      return modalSizes[size.value];
    });

    return () => (
      <Dialog.Root v-model:open={open.value}>
        <Dialog.Content
          backdrop={!noBackdrop.value}
          disableOutsidePointerEvents={disableOutsideClose.value}
          class={style.value}
          showClose={showClose.value}
          onCloseAutoFocus={() => {
            open.value = false;
          }}
          onPointerDownOutside={(e) => {
            if (disableOutsideClose.value) {
              e.preventDefault();
            }
          }}
        >
          {activeModal.value ? (
            wrapper.value ? (
              <>
                <Dialog.Header>
                  <Dialog.Title>{wrapper.value.title}</Dialog.Title>
                  {wrapper.value.description && (
                    <Dialog.Description>{wrapper.value.description}</Dialog.Description>
                  )}
                </Dialog.Header>
                <Form>
                  <Dialog.Body>{renderComponent(activeModal.value)}</Dialog.Body>
                  <Dialog.Footer>
                    <Button variant="secondary" onClick={closeModal}>
                      {$t("cancel")}
                    </Button>
                    <Button onClick={wrapper.value.onSubmit}>
                      {wrapper.value.btnLabel ?? $t("create")}
                    </Button>
                  </Dialog.Footer>
                </Form>
              </>
            ) : (
              renderComponent(activeModal.value)
            )
          ) : null}
        </Dialog.Content>
      </Dialog.Root>
    );
  },
});
